// "War Room": the slate. Today's games per sport as rsh serves them, with the top of book and whatever
// instances already sit on each; pick games, pick a kind and a preset, and deploy to all of them in one
// call (created on the engine, armed if asked, bot users round-robin). The same grid then shows each game's
// instances with state and exposure, with per-game and bulk disarm. Presets live in this browser only.

import { Fragment, useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Crosshair, Save, Trash2 } from 'lucide-react';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { rustLane, fmt, type DeployResult, type Game, type Instance, type Json, type Kind } from '@/lib/rustLane/api';
import { ArmedButton } from '@/views/StreamRift/armedButton';
import { GameBoard } from './board';

const SPORTS = ['ATP', 'WTA', 'ESPORTS', 'CFB', 'NFL', 'NBA', 'NHL'];
const GAMES_MS = 10_000;
const INST_MS = 2_000;
const PRESET_KEY = 'warroom.presets.v1';

type Presets = Record<string, { kind: string; params: Json; unit: number }>;

function loadPresets(): Presets {
  try { const raw = localStorage.getItem(PRESET_KEY); return raw ? (JSON.parse(raw) as Presets) : {}; } catch { return {}; }
}
function savePresets(p: Presets) {
  try { localStorage.setItem(PRESET_KEY, JSON.stringify(p)); } catch { /* per-viewer convenience only */ }
}

export default function WarRoomView() {
  const [sport, setSport] = useState('ATP');
  const [q, setQ] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [status, setStatus] = useState<Json | null>(null);
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [kind, setKind] = useState('');
  const [market, setMarket] = useState<'moneyline' | 'spread' | 'total'>('moneyline');
  const [params, setParams] = useState('{}');
  const [unit, setUnit] = useState(25);
  const [mode, setMode] = useState<'shadow' | 'live'>('shadow');
  const [arm, setArm] = useState(true);
  const [users, setUsers] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [presets, setPresets] = useState<Presets>(() => loadPresets());
  const [presetName, setPresetName] = useState('');
  const [results, setResults] = useState<DeployResult[] | null>(null);
  const [busy, setBusy] = useState(false);

  const pollGames = useCallback(async (signal: AbortSignal) => {
    try {
      const g = await rustLane.games(sport, q, signal);
      if (signal.aborted) return;
      setGames(g.filter((x) => x.markets?.some((m) => m.class === 'moneyline' && m.period === 'full')));
      setErr(null);
    } catch (e) { if (!signal.aborted) setErr(e instanceof Error ? e.message : 'failed'); }
  }, [sport, q]);
  useVisibilityAwarePoll(pollGames, GAMES_MS);

  const pollInst = useCallback(async (signal: AbortSignal) => {
    try {
      const [list, st] = await Promise.all([rustLane.instances(signal), rustLane.status(signal)]);
      if (signal.aborted) return;
      setInstances(list);
      setStatus(st);
    } catch (e) { if (!signal.aborted) setErr(e instanceof Error ? e.message : 'failed'); }
  }, []);
  const { refresh: refreshInst } = useVisibilityAwarePoll(pollInst, INST_MS);

  const pollKinds = useCallback(async (signal: AbortSignal) => {
    try {
      const k = await rustLane.kinds(signal);
      if (signal.aborted) return;
      setKinds(k);
      setKind((cur) => {
        if (cur) return cur;
        const first = k.find((x) => x.name === 'game_mm') ?? k[0];
        if (first) setParams(JSON.stringify(first.example ?? first.params ?? {}, null, 2));
        return first?.name ?? '';
      });
    } catch (e) { if (!signal.aborted) setErr(e instanceof Error ? e.message : 'failed'); }
  }, []);
  useVisibilityAwarePoll(pollKinds, 60_000);

  const botUsers = useMemo(() => {
    const u = status?.users;
    const names = Array.isArray(u) ? u.map((x) => (typeof x === 'string' ? x : String((x as Json).name ?? ''))).filter(Boolean)
      : u && typeof u === 'object' ? Object.keys(u as Json) : Array.from(new Set(instances.map((i) => i.user)));
    return names.sort();
  }, [status, instances]);
  const effUsers = users.size > 0 ? [...users].filter((u) => botUsers.includes(u)) : botUsers;

  const byGame = useMemo(() => {
    const m = new Map<string, Instance[]>();
    for (const i of instances) {
      const g = i.game ?? (i.target?.game as string | undefined);
      if (!g) continue;
      m.set(g, [...(m.get(g) ?? []), i]);
    }
    return m;
  }, [instances]);

  const toggle = (key: string) => setSelected((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const selectAll = () => setSelected(new Set(games.map((g) => g.key)));

  const deploy = async () => {
    let p: Json;
    try { p = JSON.parse(params) as Json; } catch { setNotice('params is not valid JSON'); return; }
    if (kind === 'pair_harvest') p = { ...p, market };
    if (!kind || selected.size === 0 || effUsers.length === 0) { setNotice('pick a kind, at least one game and a bot user'); return; }
    setBusy(true);
    try {
      const r = await rustLane.deploy({ kind, games: [...selected], users: effUsers, params: p, unit_usd: Math.max(1, Math.round(unit)), mode, arm, reason: reason || 'war room' });
      setResults(r.results);
      setNotice(`deployed: ${r.created} created, ${r.armed} armed, ${r.results.filter((x) => x.error).length} failed`);
      setSelected(new Set());
      await refreshInst();
    } catch (e) { setNotice(`deploy: ${e instanceof Error ? e.message : 'failed'}`); } finally { setBusy(false); }
  };

  const disarmGame = async (gameKey: string) => {
    const list = (byGame.get(gameKey) ?? []).filter((i) => i.armed);
    for (const i of list) { try { await rustLane.action(i.id, 'disarm', 'war room'); } catch (e) { setNotice(`disarm ${i.id.slice(0, 8)}: ${e instanceof Error ? e.message : 'failed'}`); } }
    setNotice(`disarmed ${list.length} on ${fmt.gameLabel(gameKey)}`);
    await refreshInst();
  };
  const disarmSelected = async () => {
    let n = 0;
    for (const g of selected) for (const i of (byGame.get(g) ?? []).filter((x) => x.armed)) { try { await rustLane.action(i.id, 'disarm', 'war room'); n++; } catch { /* reported in the next poll */ } }
    setNotice(`disarmed ${n}`);
    await refreshInst();
  };

  const kill = status?.kill ?? null;
  const account = (status?.account ?? null) as Json | null;
  const risk = (status?.risk ?? null) as Json | null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-border flex flex-wrap items-center gap-x-5 gap-y-2 border-b px-4 py-2 font-mono text-xs">
        <div className="flex items-center gap-2"><Crosshair className="h-4 w-4 text-orange-400" /><span className="text-sm font-bold">War Room</span><span className="text-muted-foreground">pick games, deploy strategies</span></div>
        <span><span className="text-muted-foreground">cash </span>{fmt.usd(account?.cashUsd)}</span>
        <span><span className="text-muted-foreground">exposure </span>{fmt.usd(risk?.accountUsd)}</span>
        <span><span className="text-muted-foreground">instances </span>{instances.filter((i) => i.armed && i.mode === 'live').length} live · {instances.filter((i) => i.armed && i.mode === 'shadow').length} shadow · {instances.filter((i) => !i.armed).length} off</span>
        {kill && <span className="rounded bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-400">KILL LATCHED</span>}
        <div className="ml-auto flex gap-2">
          <ArmedButton label="kill strategies" armedLabel="confirm: cancel every rp1- order" tone="danger" onFire={async () => { try { await rustLane.kill('strategies', 'war room'); setNotice('kill: ok'); } catch (e) { setNotice(`kill: ${e instanceof Error ? e.message : 'failed'}`); } await refreshInst(); }} />
        </div>
      </div>
      {(err || notice) && (
        <div className={`flex items-center gap-2 px-4 py-1.5 text-xs ${err ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}><AlertTriangle className="h-3.5 w-3.5" /> {err ?? notice}</div>
      )}

      <div className="grid min-h-0 flex-1 xl:grid-cols-[minmax(0,1fr)_380px]">
        {/* slate */}
        <main className="flex min-h-0 flex-col">
          <div className="border-border flex flex-wrap items-center gap-2 border-b px-3 py-2 font-mono text-xs">
            {SPORTS.map((s) => (
              <button key={s} type="button" onClick={() => { setSport(s); setSelected(new Set()); }} className={`rounded px-2 py-1 ${sport === s ? 'bg-muted font-semibold' : 'hover:bg-accent'}`}>{s}</button>
            ))}
            <input id="wr-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search" className="bg-muted ml-2 rounded px-2 py-1" />
            <span className="text-muted-foreground ml-auto">{games.length} games · {selected.size} selected</span>
            <button type="button" onClick={selectAll} className="bg-muted hover:bg-accent rounded px-2 py-1">select all</button>
            <button type="button" onClick={() => setSelected(new Set())} className="bg-muted hover:bg-accent rounded px-2 py-1">clear</button>
            {selected.size > 0 && <ArmedButton label={`disarm selected`} armedLabel={`confirm disarm on ${selected.size} game(s)`} onFire={disarmSelected} />}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full font-mono text-xs">
              <thead className="bg-background sticky top-0"><tr className="text-muted-foreground text-left"><th className="px-3 py-1.5 font-normal"></th><th className="py-1.5 font-normal">game</th><th className="py-1.5 font-normal">date</th><th className="py-1.5 font-normal">moneyline (bid / ask)</th><th className="py-1.5 font-normal">instances</th><th className="py-1.5 font-normal">board</th><th className="py-1.5 font-normal"></th></tr></thead>
              <tbody>
                {games.map((g) => {
                  const ml = g.markets.filter((m) => m.class === 'moneyline' && m.period === 'full').slice(0, 2);
                  const inst = byGame.get(g.key) ?? [];
                  const armedN = inst.filter((i) => i.armed).length;
                  return (
                    <Fragment key={g.key}>
                    <tr className={`border-border border-b ${selected.has(g.key) ? 'bg-muted/60' : 'hover:bg-accent/40'}`}>
                      <td className="px-3 py-1.5"><input id={`wr-${g.key}`} type="checkbox" checked={selected.has(g.key)} onChange={() => toggle(g.key)} /></td>
                      <td className="py-1.5 font-semibold">{fmt.gameLabel(g.key)}</td>
                      <td className="text-muted-foreground py-1.5">{g.key.split('|')[1]}</td>
                      <td className="py-1.5">{ml.map((m) => <span key={m.ticker} className="mr-3"><span className="text-muted-foreground">{m.ticker.split('-').pop()}</span> {fmt.cents(m.yesBid)} / {fmt.cents(m.yesAsk)}{m.status && m.status !== 'active' ? <span className="ml-1 text-amber-400">{m.status}</span> : null}</span>)}</td>
                      <td className="py-1.5">{inst.length === 0 ? <span className="text-muted-foreground">—</span> : inst.map((i) => (
                        <span key={i.id} className={`mr-1 inline-block rounded px-1.5 py-0.5 text-[10.5px] ${!i.armed ? 'bg-muted text-muted-foreground' : i.mode === 'live' ? 'bg-orange-500/15 text-orange-400' : 'bg-sky-500/15 text-sky-400'}`}>{i.kind} · {i.user} · {i.armed ? i.mode : 'off'} · {fmt.usd(i.exposureUsd)}{i.blocked ? ' · blocked' : ''}</span>
                      ))}</td>
                      <td className="py-1.5"><button type="button" className={`rounded px-1.5 py-0.5 ${expanded === g.key ? 'bg-muted font-semibold' : 'hover:bg-accent'}`} onClick={() => setExpanded(expanded === g.key ? null : g.key)}>{expanded === g.key ? 'close' : inst.some((i) => i.kind === 'scanner') ? 'open' : 'no scanner'}</button></td>
                      <td className="py-1.5 pr-3 text-right">{armedN > 0 && <ArmedButton label="disarm" armedLabel={`confirm disarm ${armedN}`} onFire={() => disarmGame(g.key)} />}</td>
                    </tr>
                    {expanded === g.key && <tr className="border-border border-b"><td colSpan={7} className="bg-muted/20 px-3 py-2"><GameBoard gameKey={g.key} instances={inst} /></td></tr>}
                    </Fragment>
                  );
                })}
                {games.length === 0 && <tr><td colSpan={7} className="text-muted-foreground px-3 py-4">No {sport} games with a full-game moneyline on rsh right now.</td></tr>}
              </tbody>
            </table>
          </div>
        </main>

        {/* deploy panel */}
        <aside className="border-border min-h-0 overflow-y-auto border-l p-3 font-mono text-xs">
          <h3 className="text-muted-foreground mb-2 text-[11px] uppercase tracking-wide">deploy to {selected.size} selected</h3>
          <div className="space-y-2">
            <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">kind</span>
              <select id="wr-kind" value={kind} onChange={(e) => { setKind(e.target.value); const k = kinds.find((x) => x.name === e.target.value); if (k) setParams(JSON.stringify(k.example ?? k.params ?? {}, null, 2)); }} className="bg-muted rounded px-2 py-1">{kinds.map((k) => <option key={k.name} value={k.name}>{k.name}</option>)}</select></label>
            {kind === 'pair_harvest' && (
              <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">market (the pair)</span>
                <select id="wr-market" value={market} onChange={(e) => setMarket(e.target.value as 'moneyline' | 'spread' | 'total')} className="bg-muted rounded px-2 py-1">
                  <option value="moneyline">moneyline · YES + YES, the two sides of the match</option>
                  <option value="spread">spread · YES + NO of the main line</option>
                  <option value="total">total · YES + NO of the main line (spread harvester on the over/under)</option>
                </select></label>
            )}
            <div className="flex items-end gap-1">
              <label className="flex flex-1 flex-col gap-0.5"><span className="text-muted-foreground">preset</span>
                <select id="wr-preset" value="" onChange={(e) => { const p = presets[e.target.value]; if (p) { setKind(p.kind); setParams(JSON.stringify(p.params, null, 2)); setUnit(p.unit); setPresetName(e.target.value); } }} className="bg-muted rounded px-2 py-1"><option value="">load…</option>{Object.keys(presets).sort().map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
              <input id="wr-preset-name" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="name" className="bg-muted w-24 rounded px-2 py-1" />
              <button type="button" title="save preset" className="bg-muted hover:bg-accent rounded p-1.5" onClick={() => { if (!presetName) return; try { const p = JSON.parse(params) as Json; const n = { ...presets, [presetName]: { kind, params: p, unit } }; setPresets(n); savePresets(n); setNotice(`preset ${presetName} saved (this browser)`); } catch { setNotice('params is not valid JSON'); } }}><Save className="h-3.5 w-3.5" /></button>
              <button type="button" title="delete preset" className="bg-muted hover:bg-accent rounded p-1.5" onClick={() => { if (!presetName || !presets[presetName]) return; const n = { ...presets }; delete n[presetName]; setPresets(n); savePresets(n); setPresetName(''); }}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">params</span>
              <textarea id="wr-params" value={params} onChange={(e) => setParams(e.target.value)} className="bg-muted h-44 rounded px-2 py-1" spellCheck={false} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">unit $ / game</span>
                <input id="wr-unit" type="number" min={1} step={1} value={unit} onChange={(e) => setUnit(Number(e.target.value))} className="bg-muted rounded px-2 py-1" /></label>
              <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">mode</span>
                <select id="wr-mode" value={mode} onChange={(e) => setMode(e.target.value as 'shadow' | 'live')} className="bg-muted rounded px-2 py-1"><option value="shadow">shadow</option><option value="live">live (real orders)</option></select></label>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">bot users (round-robin; none ticked = all {botUsers.length})</div>
              <div className="flex flex-wrap gap-1">{botUsers.map((u) => (
                <label key={u} className={`cursor-pointer rounded px-1.5 py-0.5 ${users.has(u) ? 'bg-muted font-semibold' : 'bg-muted/40'}`}><input type="checkbox" className="mr-1" checked={users.has(u)} onChange={() => setUsers((s) => { const n = new Set(s); if (n.has(u)) n.delete(u); else n.add(u); return n; })} />{u}</label>
              ))}</div>
            </div>
            <label className="flex items-center gap-2"><input id="wr-arm" type="checkbox" checked={arm} onChange={(e) => setArm(e.target.checked)} /> arm immediately</label>
            <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">reason (logged)</span>
              <input id="wr-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-muted rounded px-2 py-1" placeholder="cfb slate" /></label>
            {mode === 'live' && arm
              ? <ArmedButton label={`deploy LIVE to ${selected.size}`} armedLabel={`confirm: real orders on ${selected.size} game(s), $${unit} each`} tone="danger" onFire={deploy} />
              : <button type="button" disabled={busy || selected.size === 0} onClick={() => void deploy()} className="w-full rounded bg-emerald-500/15 px-2 py-1.5 font-semibold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50">{busy ? 'deploying…' : `deploy ${mode}${arm ? ', armed' : ', disarmed'} to ${selected.size}`}</button>}
          </div>
          {results && (
            <div className="mt-4">
              <h3 className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">last deploy</h3>
              {results.map((r) => (
                <div key={r.game} className="flex justify-between gap-2 border-b border-border py-1"><span className="truncate">{fmt.gameLabel(r.game)} <span className="text-muted-foreground">{r.user}</span></span><span className={r.error ? 'text-red-400' : r.armed ? 'text-emerald-400' : 'text-sky-400'}>{r.error ?? (r.armed ? 'armed' : 'created')}</span></div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
